import { Typography } from '@mui/material';
import { EPersona } from '@sharedInterfaces/EPersona';
import { EDashboard, ELinks } from '@sharedInterfaces/globalEnums';
import { isEDashboard } from '@sharedInterfaces/IDashboard';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import editDashboard from '@src/APIs/graphQl/Dashboard/editDashboard';
import getDashboard from '@src/APIs/graphQl/Dashboard/getDashboard';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import InnerLayout from '@src/App/Components/Core/Layouts/InnerLayout/InnerLayout';
import MvpSubNav, { type IMvpQuickAction, type IMvpRole } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import type { AppState } from '@store/store';
import { BarChart3, ClipboardList, Home, Settings, User, UserCircle, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Dashboard, { type DashboardState } from './Dashboard/Dashboard';
import styles from './DashboardPage.module.css';
import AdminDashboardSection from './DashboardSections/AdminDashboardSection';
import ChatAgentSection from './DashboardSections/ChatAgentSection';
import EmployeeDashboardSection from './DashboardSections/EmployeeDashboardSection';
import ManagerDashboardSection from './DashboardSections/ManagerDashboardSection';

interface MvpDashboardPageProps {
    inDialog?: boolean;
}

export default function MvpDashboardPage({ inDialog }: MvpDashboardPageProps) {
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).dashboard;
    const employee = useSelector((state: AppState) => state.employee);
    const personas = useSelector((state: AppState) => state.personas);
    const { tab } = useParams();
    const [loadingError, setLoadingError] = useState<CustomGraphQLError | null>(null);
    const [isEditing, setIsEditing] = useState(false);

    // Get dashboard preference from session or use tab parameter
    const getStoredDashboard = (): EDashboard => {
        if (tab && isEDashboard(tab)) return tab;

        // Map URL path to dashboard type
        const currentPath = window.location.pathname;
        if (currentPath.includes(`/${ELinks.DASHBOARD_MANAGER}`)) return EDashboard.MANAGER;
        if (currentPath.includes(`/${ELinks.DASHBOARD_ADMIN}`)) return EDashboard.ADMIN;
        if (currentPath.includes(`/${ELinks.DASHBOARD_EMPLOYEE}`)) return EDashboard.EMPLOYEE;

        const stored = sessionStorage.getItem('preferredDashboard');
        return stored && isEDashboard(stored) ? stored : EDashboard.EMPLOYEE;
    };

    const dashboardId: EDashboard = getStoredDashboard();

    // Role switcher configuration
    const getAvailableRoles = (): IMvpRole[] => {
        const roles: IMvpRole[] = [
            {
                id: 'employee',
                label: 'Mitarbeiter',
                icon: <User size={18} />,
            },
        ];

        if (personas.includes(EPersona.FK)) {
            roles.push({
                id: 'manager',
                label: 'Manager',
                icon: <ClipboardList size={18} />,
            });
        }

        if (personas.includes(EPersona.Admin)) {
            roles.push({
                id: 'admin',
                label: 'Admin',
                icon: <Users size={18} />,
            });
        }

        return roles;
    };

    const handleRoleChange = (roleId: string) => {
        switch (roleId) {
            case 'employee':
                navigate(`/${ELinks.DASHBOARD_EMPLOYEE}`);
                break;
            case 'manager':
                navigate(`/${ELinks.DASHBOARD_MANAGER}`);
                break;
            case 'admin':
                navigate(`/${ELinks.DASHBOARD_ADMIN}`);
                break;
        }
    };

    const getCurrentRole = (): string => {
        switch (dashboardId) {
            case EDashboard.MANAGER:
                return 'manager';
            case EDashboard.ADMIN:
                return 'admin';
            default:
                return 'employee';
        }
    };

    const getQuickActionsForRole = (roleId: string): IMvpQuickAction[] => {
        switch (roleId) {
            case 'employee':
                return [
                    {
                        id: 'profile',
                        label: 'Profil',
                        icon: <UserCircle size={18} />,
                        onClick: () => navigate(`/${ELinks.PROFILE}`),
                        variant: 'primary',
                    },
                    {
                        id: 'settings',
                        label: 'Einstellungen',
                        icon: <Settings size={18} />,
                        onClick: () => navigate(`/${ELinks.PROFILE_EDIT}`),
                    },
                ];
            case 'manager':
                return [
                    {
                        id: 'team',
                        label: 'Team',
                        icon: <Users size={18} />,
                        onClick: () => navigate(`/${ELinks.MY_TEAM}`),
                        variant: 'primary',
                    },
                    {
                        id: 'opportunities',
                        label: 'Anfragen',
                        icon: <ClipboardList size={18} />,
                        onClick: () => navigate('/sales/opportunities/list'),
                    },
                    {
                        id: 'profile',
                        label: 'Profil',
                        icon: <UserCircle size={18} />,
                        onClick: () => navigate(`/${ELinks.PROFILE}`),
                    },
                ];
            case 'admin':
                return [
                    {
                        id: 'analytics',
                        label: 'Analytics',
                        icon: <BarChart3 size={18} />,
                        onClick: () => console.log('Admin Analytics clicked'),
                        variant: 'primary',
                    },
                    {
                        id: 'settings',
                        label: 'Einstellungen',
                        icon: <Settings size={18} />,
                        onClick: () => navigate(`/${ELinks.SETTINGS}`),
                    },
                ];
            default:
                return [];
        }
    };

    const availableRoles = getAvailableRoles();

    // Store dashboard preference when it changes
    React.useEffect(() => {
        if (tab && isEDashboard(tab)) {
            sessionStorage.setItem('preferredDashboard', tab);
        }
    }, [tab]);
    const originalDashboardState = useSelector((state: AppState) => state.dashboard.find((d) => d.id === dashboardId));

    const handleEditToggle = async (dashboardState: DashboardState) => {
        console.log('toggle', isEditing);
        const hasChanges = JSON.stringify(originalDashboardState) !== JSON.stringify(dashboardState);
        if (hasChanges && originalDashboardState) {
            await editDashboard(
                dashboardId,
                originalDashboardState.title,
                originalDashboardState.description,
                JSON.stringify(dashboardState.layout),
                JSON.stringify(dashboardState.widgets),
            );
        }
        setIsEditing(!isEditing);
        return Promise.resolve();
    };

    const errorMessage = loadingError
        ? loadingError.statusCode === 401
            ? langStrings.oftenUsed.notAllowed
            : loadingError.statusCode === 408
              ? langStrings.oftenUsed.offline
              : loadingError.message
        : null;

    useEffect(() => {
        if (inDialog) return;
        console.log('Dashboard ID:', dashboardId, 'Tab:', tab, 'isEDashboard:', isEDashboard(tab));
        getDashboard(dashboardId).catch((ex) => {
            console.warn('Dashboard not found:', dashboardId, ex);
            // Für ADMIN Dashboard erstmal keinen Fehler anzeigen (noch nicht im Backend)
            if (dashboardId === EDashboard.ADMIN) {
                console.log('Dashboard', dashboardId, 'not found in backend, continuing without error');
                return;
            }
            setLoadingError(ex);
        });
    }, [inDialog, dashboardId]);

    /* // Erstelle Buttons für die Tabs
    const dashboardButtons = (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<EditIcon />}
                href={`/${ELinks.PROFILE_EDIT}`}
            >
                {langStrings.oftenUsed?.edit || "Bearbeiten"}
            </Button>
        </Box>
    ); */

    return (
        <>
            {availableRoles.length > 1 && (
                <MvpSubNav
                    currentPageTitle="Dashboard"
                    roles={availableRoles}
                    activeRole={getCurrentRole()}
                    onRoleChange={handleRoleChange}
                    quickActions={getQuickActionsForRole(getCurrentRole())}
                />
            )}
            <InnerLayout
                title={employee ? `Willkommen zurück, ${employee.firstName}!` : 'Dashboard'}
                subTitle="Hier findest du deine wichtigsten Widgets auf einen Blick"
            >
                {errorMessage && <Typography>{errorMessage}</Typography>}
                {employee && !errorMessage && (
                    <>
                        {dashboardId !== EDashboard.ADMIN && <ChatAgentSection />}
                        {dashboardId === EDashboard.EMPLOYEE && <EmployeeDashboardSection />}
                        {dashboardId === EDashboard.MANAGER && <ManagerDashboardSection />}
                        {dashboardId === EDashboard.ADMIN && <AdminDashboardSection />}
                        {(dashboardId === EDashboard.EMPLOYEE || dashboardId === EDashboard.MANAGER) && (
                            <Dashboard
                                dashboardId={dashboardId}
                                isEditing={isEditing}
                                showEditButton
                                onEditToggle={handleEditToggle}
                            />
                        )}
                    </>
                )}
                {!employee && !errorMessage && <LoadingBox />}
            </InnerLayout>
        </>
    );
}
